import { z } from 'zod';
import { AUDIT_ACTIONS, AUDIT_ENTITIES, CALL_LANGUAGE_CODES, CALL_OUTCOMES, CALL_STATUSES, CAMPAIGN_STATUSES, LEAD_STATUSES, USER_ROLES, VOICE_PROVIDERS, } from './enums.js';
import { PERMISSION_KEYS } from './permissions.js';
// ---------- Auth ----------
/** Política de senha: 8+ caracteres, com letra e número. Vale para criação, troca e redefinição. */
export const passwordSchema = z
    .string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .max(128, 'A senha deve ter no máximo 128 caracteres')
    .regex(/[A-Za-z]/, 'A senha deve conter pelo menos uma letra')
    .regex(/\d/, 'A senha deve conter pelo menos um número');
export const loginSchema = z.object({
    email: z.string().trim().email('E-mail inválido'),
    password: z.string().min(1, 'Informe a senha'),
});
export const userSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(USER_ROLES),
    active: z.boolean(),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
    lockedUntil: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Usuário da sessão: o que o painel precisa para decidir o que mostrar. */
export const sessionUserSchema = userSchema
    .pick({ id: true, email: true, name: true, role: true, mustChangePassword: true })
    .extend({ permissions: z.array(z.enum(PERMISSION_KEYS)) });
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Informe a senha atual'),
    newPassword: passwordSchema,
});
export const updateProfileSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
});
export const sessionSchema = z.object({
    id: z.string().uuid(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime(),
    current: z.boolean(),
});
// ---------- Users (gestão) ----------
export const userCreateSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: z.string().trim().email('E-mail inválido').max(254),
    password: passwordSchema,
    role: z.enum(USER_ROLES),
    /** Por padrão o usuário troca a senha no primeiro acesso. */
    mustChangePassword: z.boolean().default(true),
});
export const userUpdateSchema = z
    .object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: z.string().trim().email('E-mail inválido').max(254),
    role: z.enum(USER_ROLES),
    active: z.boolean(),
})
    .partial();
export const userResetPasswordSchema = z.object({
    password: passwordSchema,
    mustChangePassword: z.boolean().default(true),
});
export const userListItemSchema = userSchema.extend({ activeSessions: z.number().int() });
// ---------- Roles ----------
export const roleSchema = z.object({
    key: z.enum(USER_ROLES),
    name: z.string(),
    description: z.string(),
    /** `super_admin`: sempre todas as permissões, sem linha editável. */
    fixed: z.boolean(),
    /** Quem consulta não pode editar este perfil (fixo, ou `admin` visto por um não-super). */
    locked: z.boolean(),
    permissions: z.array(z.enum(PERMISSION_KEYS)),
    userCount: z.number().int(),
    updatedAt: z.string().datetime().nullable(),
});
export const rolePermissionsInputSchema = z.object({
    permissions: z.array(z.enum(PERMISSION_KEYS)),
});
// ---------- Audit ----------
export const auditChangeSchema = z.object({ from: z.unknown(), to: z.unknown() });
export const auditLogSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    actorId: z.string().uuid().nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    action: z.enum(AUDIT_ACTIONS),
    entity: z.enum(AUDIT_ENTITIES),
    entityId: z.string().nullable(),
    entityLabel: z.string().nullable(),
    changes: z.record(z.string(), auditChangeSchema).nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    requestId: z.string().nullable(),
});
// ---------- Pagination ----------
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    sortBy: z.string().optional(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export const paginatedSchema = (item) => z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const auditFilterSchema = paginationQuerySchema.extend({
    action: z.enum(AUDIT_ACTIONS).optional(),
    entity: z.enum(AUDIT_ENTITIES).optional(),
    entityId: z.string().optional(),
    actorId: z.string().uuid().optional(),
    /** Busca em nome/e-mail do ator e no rótulo da entidade. */
    q: z.string().trim().max(200).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
// ---------- Product ----------
// ---------- Brand (marca: agrupa os codenames de um mesmo produto) ----------
export const brandSchema = z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    /** Base de conhecimento no provedor de voz; marca com base ganha agente próprio. */
    knowledgeBaseId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const brandInputSchema = brandSchema
    .pick({ name: true, description: true, knowledgeBaseId: true })
    .partial()
    .extend({ name: z.string().min(1).optional() });
export const knowledgeBaseInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['in_progress', 'complete', 'error']),
    sources: z.number().int(),
});
/** Agente criado no provedor de voz: um por script/idioma/marca. */
export const providerAgentSchema = z.object({
    id: z.string().uuid(),
    provider: z.string(),
    name: z.string(),
    brandSlug: z.string().nullable(),
    agentId: z.string(),
    updatedAt: z.string().datetime(),
});
/** Cupom da BuyGoods: letras, números, _ ou -, de 2 a 40 caracteres (a API guarda em maiúsculas). */
export const COUPON_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{1,39}$/;
export const productSchema = z.object({
    id: z.string().uuid(),
    codename: z.string().min(1),
    name: z.string().min(1),
    sourceSlug: z.string().nullable(),
    checkoutUrl: z.string().url().nullable(),
    price: z.number().nullable(),
    currency: z.string().length(3).default('USD'),
    offerText: z.string().nullable(),
    couponCode: z
        .string()
        .regex(COUPON_CODE_PATTERN, 'Cupom inválido: use letras, números, _ ou -, de 2 a 40 caracteres')
        .nullable(),
    description: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const productInputSchema = productSchema
    .omit({ id: true, createdAt: true, updatedAt: true })
    .partial({
    active: true,
    currency: true,
    sourceSlug: true,
    checkoutUrl: true,
    price: true,
    offerText: true,
    couponCode: true,
    description: true,
});
/** Ativar/desativar vários de uma vez — liberação gradual dos produtos para campanha. */
export const productBulkActiveSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(500),
    active: z.boolean(),
});
// ---------- Lead ----------
export const leadSchema = z.object({
    id: z.string().uuid(),
    externalId: z.string().nullable(),
    name: z.string().nullable(),
    phoneRaw: z.string(),
    phoneE164: z.string(),
    /** Nulo = ainda não validado (lead anterior ao campo; o worker revalida em lotes). */
    phoneValid: z.boolean().nullable().optional(),
    email: z.string().nullable(),
    country: z.string().nullable(),
    countryIso: z.string().nullable(),
    state: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    productCodename: z.string().nullable(),
    affId: z.string().nullable(),
    sourceSlug: z.string().nullable(),
    status: z.enum(LEAD_STATUSES),
    attempts: z.number().int(),
    lastCallAt: z.string().datetime().nullable(),
    receivedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    product: productSchema
        .pick({
        id: true,
        codename: true,
        name: true,
        active: true,
        offerText: true,
        checkoutUrl: true,
        couponCode: true,
    })
        .nullable()
        .optional(),
});
export const leadInputSchema = z.object({
    name: z.string().trim().min(1).nullable().optional(),
    phone: z.string().trim().min(7),
    email: z.string().trim().email().nullable().optional(),
    country: z.string().trim().nullable().optional(),
    state: z.string().trim().nullable().optional(),
    city: z.string().trim().nullable().optional(),
    address: z.string().trim().nullable().optional(),
    productCodename: z.string().trim().nullable().optional(),
    affId: z.string().trim().nullable().optional(),
    sourceSlug: z.string().trim().nullable().optional(),
    externalId: z.string().trim().nullable().optional(),
    receivedAt: z.string().datetime().optional(),
});
export const leadFilterSchema = paginationQuerySchema.extend({
    search: z.string().optional(),
    status: z.enum(LEAD_STATUSES).optional(),
    sourceSlug: z.string().optional(),
    productCodename: z.string().optional(),
    country: z.string().optional(),
    /** valid = telefone confere; invalid = número que não existe; pending = ainda não validado. */
    phoneValid: z.enum(['valid', 'invalid', 'pending']).optional(),
});
// ---------- Script template ----------
export const scriptTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    language: z.enum(CALL_LANGUAGE_CODES),
    voiceId: z.string().nullable(),
    firstMessage: z.string().min(1),
    systemPrompt: z.string().min(10),
    isDefault: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const scriptTemplateInputSchema = scriptTemplateSchema
    .omit({ id: true, createdAt: true, updatedAt: true })
    .partial({ isDefault: true, voiceId: true });
/** Variáveis disponíveis nos templates ({{nome}}). */
export const SCRIPT_VARIABLES = [
    'lead_name',
    'lead_first_name',
    'lead_greeting',
    'lead_email',
    'product_name',
    'product_description',
    'product_price',
    'offer',
    'checkout_url',
    'company_name',
    'agent_name',
    'sms_rule',
    'email_rule',
    'coupon_code',
    'coupon_rule',
];
// ---------- Settings ----------
/**
 * Nome da tabela de origem, com schema opcional (`public.webhook_events`). Vai para o SQL
 * como identificador citado, então nada além disto pode entrar.
 */
export const SOURCE_TABLE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)?$/;
/**
 * Modelos de linguagem que o Retell oferece para o agente, pelos ids da API do Retell (não os
 * dos fornecedores). Lista curada à mão: o SDK instalado só tipa os antigos e a API aceita os
 * novos. Preço por minuto e disponibilidade mudam com o tempo — conferir em retellai.com/pricing.
 */
export const LLM_MODEL_IDS = [
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-5.4',
    'gpt-5.5',
    'gpt-5.6-terra',
    'claude-4.6-sonnet',
    'claude-5-sonnet',
    'claude-5.5-sonnet',
    'claude-5-opus',
    'claude-5.5-opus',
    'gemini-3.5-flash',
];
export const LLM_MODEL_LABELS = {
    'gpt-4.1': 'GPT-4.1 (OpenAI)',
    'gpt-4.1-mini': 'GPT-4.1 mini (OpenAI)',
    'gpt-5.4': 'GPT-5.4 (OpenAI)',
    'gpt-5.5': 'GPT-5.5 (OpenAI)',
    'gpt-5.6-terra': 'GPT-5.6 Terra (OpenAI)',
    'claude-4.6-sonnet': 'Claude 4.6 Sonnet (Anthropic)',
    'claude-5-sonnet': 'Claude 5 Sonnet (Anthropic)',
    'claude-5.5-sonnet': 'Claude 5.5 Sonnet (Anthropic)',
    'claude-5-opus': 'Claude 5 Opus (Anthropic)',
    'claude-5.5-opus': 'Claude 5.5 Opus (Anthropic)',
    'gemini-3.5-flash': 'Gemini 3.5 Flash (Google)',
};
export const settingsSchema = z.object({
    companyName: z.string().min(1),
    agentName: z.string().min(1),
    callLanguage: z.enum(CALL_LANGUAGE_CODES),
    voiceProvider: z.enum(VOICE_PROVIDERS),
    /** Modelo de linguagem dos agentes no provedor; vale para todos os agentes. */
    llmModel: z.enum(LLM_MODEL_IDS),
    fromNumber: z.string().nullable(),
    timezone: z.string().min(1),
    callWindowStart: z.string().regex(/^\d{2}:\d{2}$/),
    callWindowEnd: z.string().regex(/^\d{2}:\d{2}$/),
    maxAttempts: z.number().int().min(1).max(10),
    retryIntervalMin: z.number().int().min(1).max(10080),
    maxConcurrentCalls: z.number().int().min(1).max(500),
    callsPerMinute: z.number().int().min(1).max(1000),
    defaultOfferText: z.string().nullable(),
    smsEnabled: z.boolean(),
    // Twilio — envio do link de checkout por SMS
    twilioAccountSid: z.string().nullable(),
    twilioApiKeySid: z.string().nullable(),
    twilioMessagingServiceSid: z.string().nullable(),
    /** Pares "número de teste → celular", um por linha; validado e normalizado ao salvar. */
    smsTestCopies: z.string().nullable(),
    // E-mail (Resend) — segunda via do link de checkout
    emailEnabled: z.boolean(),
    emailFromAddress: z.string().email().nullable(),
    emailFromName: z.string().nullable(),
    emailReplyTo: z.string().email().nullable(),
    // Integração — banco de origem dos carrinhos abandonados (buygoods_bridge)
    sourceSyncEnabled: z.boolean(),
    sourceDbHost: z.string().nullable(),
    sourceDbPort: z.number().int().min(1).max(65535),
    sourceDbName: z.string().nullable(),
    sourceDbUser: z.string().nullable(),
    sourceDbSsl: z.boolean(),
    sourceTable: z
        .string()
        .regex(SOURCE_TABLE_PATTERN, 'Nome de tabela inválido (use letras, números e _, com schema opcional)'),
    sourceSyncIntervalMin: z.number().int().min(1).max(1440),
    sourceSyncSince: z.string().datetime({ offset: true }).nullable(),
    // Piloto automático
    autopilotEnabled: z.boolean(),
    autopilotChannel: z.enum(['phone', 'web']),
    autopilotLeadsPerRun: z.number().int().min(1).max(500),
    autopilotIntervalMin: z.number().int().min(1).max(1440),
    autopilotDelayMin: z.number().int().min(0).max(10080),
    autopilotMaxAgeDays: z.number().int().min(1).max(365),
    /** ISO-3166 alpha-2 separados por vírgula ("US" ou "US,CA"). */
    autopilotCountries: z
        .string()
        .regex(/^\s*[A-Za-z]{2}(\s*,\s*[A-Za-z]{2})*\s*$/, 'Use códigos de país de 2 letras separados por vírgula, ex.: US,CA'),
    // Chaves: nunca retornadas em claro; a API devolve apenas se estão configuradas
    hasRetellApiKey: z.boolean(),
    hasOpenaiApiKey: z.boolean(),
    hasTwilioApiKeySecret: z.boolean(),
    hasSourceDbPassword: z.boolean(),
    hasResendApiKey: z.boolean(),
    hasResendWebhookSecret: z.boolean(),
    retellWebhookConfigured: z.boolean(),
    webhookBaseUrl: z.string(),
});
export const settingsInputSchema = settingsSchema
    .omit({
    hasRetellApiKey: true,
    hasOpenaiApiKey: true,
    hasTwilioApiKeySecret: true,
    hasSourceDbPassword: true,
    hasResendApiKey: true,
    hasResendWebhookSecret: true,
    retellWebhookConfigured: true,
    webhookBaseUrl: true,
})
    .partial()
    .extend({
    retellApiKey: z.string().min(10).optional(),
    openaiApiKey: z.string().min(10).optional(),
    resendApiKey: z.string().min(10).optional(),
    resendWebhookSecret: z.string().min(10).optional(),
    twilioApiKeySecret: z.string().min(10).optional(),
    sourceDbPassword: z.string().min(1).optional(),
});
/** Concorrência da conta no provedor de voz (referência para "Chamadas simultâneas"). */
export const concurrencyInfoSchema = z.object({
    current: z.number().int(),
    limit: z.number().int(),
    base: z.number().int(),
    purchased: z.number().int(),
    burstEnabled: z.boolean(),
    burstLimit: z.number().int().nullable(),
});
export const providerConcurrencySchema = z.object({
    provider: z.string(),
    concurrency: concurrencyInfoSchema.nullable(),
    error: z.string().nullable(),
});
/** Campos da conexão de origem aceitos pelo "Testar conexão" sem precisar salvar antes. */
export const sourceConnectionInputSchema = settingsInputSchema.pick({
    sourceDbHost: true,
    sourceDbPort: true,
    sourceDbName: true,
    sourceDbUser: true,
    sourceDbPassword: true,
    sourceDbSsl: true,
    sourceTable: true,
});
export const sourceSyncTestSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
    serverVersion: z.string().nullable(),
    /** Colunas obrigatórias que a tabela não tem. Vazio quando a estrutura bate. */
    missingColumns: z.array(z.string()),
    /** Carrinhos abandonados com id acima do cursor atual — o que a próxima execução importa. */
    pending: z.number().int().nullable(),
    newestReceivedAt: z.string().nullable(),
});
export const sourceSyncStatusSchema = z.object({
    /** Host, banco, usuário e senha preenchidos. */
    configured: z.boolean(),
    enabled: z.boolean(),
    intervalMin: z.number().int(),
    cursor: z.string().nullable(),
    lastRunAt: z.string().nullable(),
    lastError: z.string().nullable(),
    imported: z.number().int(),
    /** Há uma execução em andamento no worker. */
    running: z.boolean(),
    /** Há uma execução manual aguardando o worker. */
    queued: z.boolean(),
    /** O worker registrou o agendamento periódico (está no ar). */
    scheduled: z.boolean(),
    nextRunAt: z.string().nullable(),
});
// ---------- Campaign ----------
export const campaignFiltersSchema = z.object({
    sourceSlug: z.string().optional(),
    productCodename: z.string().optional(),
    country: z.string().optional(),
    statuses: z.array(z.enum(LEAD_STATUSES)).optional(),
    leadIds: z.array(z.string().uuid()).optional(),
});
export const CAMPAIGN_KINDS = ['manual', 'autopilot'];
export const CALL_CHANNELS = ['phone', 'web'];
export const campaignSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    language: z.enum(CALL_LANGUAGE_CODES),
    scriptTemplateId: z.string().uuid(),
    status: z.enum(CAMPAIGN_STATUSES),
    kind: z.enum(CAMPAIGN_KINDS),
    channel: z.enum(CALL_CHANNELS),
    filters: campaignFiltersSchema,
    totalLeads: z.number().int(),
    completedLeads: z.number().int(),
    recoveredLeads: z.number().int(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const campaignInputSchema = z.object({
    name: z.string().min(1),
    language: z.enum(CALL_LANGUAGE_CODES).optional(),
    scriptTemplateId: z.string().uuid().optional(),
    filters: campaignFiltersSchema.default({}),
});
// ---------- Piloto automático ----------
/** Lead matriculado pelo piloto no canal navegador, à espera de alguém atender. */
export const autopilotPendingWebSchema = z.object({
    campaignLeadId: z.string().uuid(),
    leadId: z.string().uuid(),
    leadName: z.string().nullable(),
    phoneE164: z.string(),
    productName: z.string().nullable(),
    attempts: z.number().int(),
});
export const autopilotStatusSchema = z.object({
    enabled: z.boolean(),
    channel: z.enum(CALL_CHANNELS),
    leadsPerRun: z.number().int(),
    intervalMin: z.number().int(),
    delayMin: z.number().int(),
    maxAgeDays: z.number().int(),
    countries: z.array(z.string()),
    lastRunAt: z.string().nullable(),
    lastEnrolled: z.number().int(),
    lastEligible: z.number().int(),
    /** Elegíveis agora, pelos critérios atuais. */
    eligibleNow: z.number().int(),
    nextRunAt: z.string().nullable(),
    /** O worker está no ar (tick registrado e worker conectado). */
    workerOnline: z.boolean(),
    campaign: campaignSchema.nullable(),
    pendingWeb: z.array(autopilotPendingWebSchema),
});
// ---------- Call ----------
export const callSchema = z.object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    campaignId: z.string().uuid().nullable(),
    provider: z.enum(VOICE_PROVIDERS),
    providerCallId: z.string().nullable(),
    direction: z.enum(['outbound', 'inbound', 'web']),
    language: z.enum(CALL_LANGUAGE_CODES),
    status: z.enum(CALL_STATUSES),
    outcome: z.enum(CALL_OUTCOMES).nullable(),
    fromNumber: z.string().nullable(),
    toNumber: z.string(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
    durationSec: z.number().int().nullable(),
    transcript: z.string().nullable(),
    summary: z.string().nullable(),
    sentiment: z.string().nullable(),
    recordingUrl: z.string().nullable(),
    costUsd: z.number().nullable(),
    disconnectReason: z.string().nullable(),
    errorMessage: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    lead: leadSchema
        .pick({ id: true, name: true, phoneE164: true, productCodename: true })
        .optional(),
});
export const callFilterSchema = paginationQuerySchema.extend({
    status: z.enum(CALL_STATUSES).optional(),
    outcome: z.enum(CALL_OUTCOMES).optional(),
    campaignId: z.string().uuid().optional(),
    leadId: z.string().uuid().optional(),
    productCodename: z.string().optional(),
    language: z.enum(CALL_LANGUAGE_CODES).optional(),
    direction: z.enum(['outbound', 'inbound', 'web']).optional(),
    /** Nome do lead ou trecho do telefone. */
    search: z.string().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
/** Totais do conjunto filtrado — o que responde "quantas chamadas naquele período". */
export const callSummarySchema = z.object({
    total: z.number().int(),
    durationSec: z.number().int(),
    costUsd: z.number(),
    byStatus: z.record(z.string(), z.number().int()),
    byOutcome: z.record(z.string(), z.number().int()),
});
// ---------- Dashboard ----------
export const dashboardStatsSchema = z.object({
    leads: z.object({
        total: z.number(),
        new: z.number(),
        recovered: z.number(),
        doNotCall: z.number(),
    }),
    calls: z.object({
        today: z.number(),
        total: z.number(),
        inProgress: z.number(),
        contactRate: z.number(),
        recoveryRate: z.number(),
        avgDurationSec: z.number(),
        totalCostUsd: z.number(),
    }),
    daily: z.array(z.object({
        date: z.string(),
        calls: z.number(),
        contacted: z.number(),
        recovered: z.number(),
    })),
    outcomes: z.array(z.object({ outcome: z.string(), count: z.number() })),
});
// ---------- Realtime events ----------
export const realtimeEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('call.updated'), call: callSchema }),
    z.object({ type: z.literal('campaign.updated'), campaign: campaignSchema }),
    z.object({ type: z.literal('lead.updated'), lead: leadSchema }),
]);
// ---------- E-mail (segunda via do link) ----------
/** Idiomas com modelo próprio; qualquer outro idioma de ligação usa o en-US. */
export const EMAIL_TEMPLATE_LANGUAGES = ['pt-BR', 'en-US'];
/** Variáveis aceitas no assunto e no corpo do e-mail. */
export const EMAIL_TEMPLATE_VARIABLES = [
    'greeting',
    'first_name',
    'brand',
    'company_name',
    'product_name',
    'checkout_url',
    'coupon_code',
    'offer',
    'coupon_line',
];
export const EMAIL_TEMPLATE_FORMATS = ['text', 'html'];
export const emailTemplateSchema = z.object({
    language: z.enum(EMAIL_TEMPLATE_LANGUAGES),
    subject: z.string().min(1).max(200),
    /** `text`: corpo em texto, HTML gerado com o link como botão. `html`: o corpo é o HTML inteiro. */
    format: z.enum(EMAIL_TEMPLATE_FORMATS),
    body: z.string().min(1).max(20000),
    /** Verdadeiro quando é o texto padrão do sistema (nenhuma edição salva). */
    isDefault: z.boolean(),
});
export const emailTemplateInputSchema = emailTemplateSchema
    .pick({ subject: true, format: true, body: true })
    .refine((t) => t.body.includes('{{checkout_url}}'), {
    message: 'O corpo precisa conter {{checkout_url}}: sem o link o e-mail não serve para nada.',
    path: ['body'],
});
export const EMAIL_STATUSES = [
    'queued',
    'sent',
    'delayed',
    'delivered',
    'opened',
    'clicked',
    'bounced',
    'complained',
    'failed',
];
export const EMAIL_REASONS = ['sms_failed', 'ai_requested', 'manual'];
export const emailListFilterSchema = paginationQuerySchema.extend({
    status: z.enum(EMAIL_STATUSES).optional(),
    reason: z.enum(EMAIL_REASONS).optional(),
    search: z.string().max(100).optional(),
});
export const emailListItemSchema = z.object({
    id: z.string().uuid(),
    callId: z.string().uuid().nullable(),
    toEmail: z.string(),
    subject: z.string(),
    reason: z.string(),
    status: z.string(),
    errorMessage: z.string().nullable(),
    sentAt: z.string(),
    deliveredAt: z.string().nullable(),
    openedAt: z.string().nullable(),
    clickedAt: z.string().nullable(),
    lead: z.object({
        id: z.string().uuid(),
        name: z.string().nullable(),
        productCodename: z.string().nullable(),
    }),
    productName: z.string().nullable(),
    brandName: z.string().nullable(),
});
